import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import TextField from '@mui/material/TextField';
import CloseIcon from '@mui/icons-material/Close';
import { Modal, Button, } from '@mui/material'
import { useLazyGetCustomerSupportByIdQuery, useUpdateCustomerSupportMutation } from "../services/customerSupport";
import { showError, showToast } from "../constants/toasts";

interface ModalProps {
    open: boolean;
    onClose: () => void;
    setOpen: Dispatch<SetStateAction<boolean>>
    status: 'pending' | 'resolved';
    editId?: number | null;
    onSuccess?: () => void;
}

export default function AddReply({ open, onClose, setOpen, status, editId = null, onSuccess }: ModalProps) {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [details, setDetails] = useState<any>(null);
    const [answer, setAnswer] = useState<string>("");

    const [getById] = useLazyGetCustomerSupportByIdQuery();
    const [updateSupport] = useUpdateCustomerSupportMutation();

    useEffect(() => {
        const init = async () => {
            if (open && editId) {
                try {
                    setIsLoading(true);
                    const res = await getById({ id: editId }).unwrap();
                    const obj = res?.data || null;
                    setDetails(obj);
                    setAnswer(obj?.answer || "");
                } catch (e: any) {
                    showError(e?.data?.message || "Failed to load details");
                } finally {
                    setIsLoading(false);
                }
            } else if (!open) {
                setDetails(null);
                setAnswer("");
            }
        };
        init();
    }, [open, editId, getById]);

    const handleSubmit = async () => {
        if (!editId) return;
        try {
            setIsLoading(true);
            const body: any = { id: editId, answer: answer?.trim() };
            if (status === 'pending') {
                body.reverted_back = true;
            }
            const res: any = await updateSupport({ body, id: editId }).unwrap();
            if (res?.status === 200 || res?.status === 201) {
                showToast(status === 'pending' ? "Reply added" : "Updated successfully");
                onSuccess?.();
                setOpen(false);
            }
        } catch (e: any) {
            showError(e?.data?.message || "Something went wrong");
        } finally {
            setIsLoading(false);
        }
    };

    const handleRevertToggle = async () => {
        if (!editId) return;
        try {
            setIsLoading(true);
            const next = !(details?.reverted_back === true);
            const res: any = await updateSupport({ body: { reverted_back: next }, id: editId }).unwrap();
            if (res?.status === 200 || res?.status === 201) {
                showToast(next ? "Marked as reverted" : "Marked as not reverted");
                onSuccess?.();
                setOpen(false);
            }
        } catch (e: any) {
            showError(e?.data?.message || "Failed to update status");
        } finally {
            setIsLoading(false);
        }
    };
    return (
        <Modal
            className="modal addReply_modal"
            open={open}
            onClose={onClose}
        >
            <div className="modal-dialog">
                <div className="modal-body">
                    <div className="modal_title">
                        <h2>Add Reply</h2>
                        <div className="btn-close" onClick={() => setOpen(false)}>
                            <CloseIcon />
                        </div>
                    </div>
                    <div className="view_page gap_p">
                        <div className="w_100">
                            <h6>Query Title</h6>
                            <p>{details?.query_title || '-'}</p>
                        </div>
                        <div className="w_100">
                            <h6>Query Description</h6>
                            <p>{details?.description || '-'}</p>
                        </div>
                        {details?.attachment ? (
                            <div className="w_100">
                                <h6>Attachment</h6>
                                <figure className="passport_img">
                                    <img src={details?.attachment} alt="Attachment" />
                                </figure>
                            </div>
                        ) : null}
                    </div>
                    <form className="form" action="">
                        <div className="control_group">
                            {status === 'pending' ? (
                                <>
                                    <div className="section_title">
                                        <h2>Add Reply</h2>
                                    </div>
                                    <TextField
                                        placeholder="Enter Reply"
                                        fullWidth
                                        hiddenLabel
                                        multiline
                                        maxRows={5}
                                        minRows={5}
                                        value={answer}
                                        onChange={(e) => setAnswer(e.target.value)}
                                    />
                                </>
                            ) : (
                                <div className="view_page gap_p">
                                    <div className="w_100">
                                        <h6>Added Reply</h6>
                                        <p>{details?.answer || '-'}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="footer_btn_flex">
                            <Button className="btn btn_grey" onClick={() => setOpen(false)} disabled={isLoading}>Cancel</Button>
                            {status === 'pending' ? (
                                <Button className="btn btn_primary" onClick={handleSubmit} disabled={isLoading || !answer.trim()}>
                                    {isLoading ? 'Processing...' : 'Submit'}
                                </Button>
                            ) : (
                                <Button className="btn btn_primary" onClick={handleRevertToggle} disabled={isLoading}>
                                    {isLoading ? 'Processing...' : (details?.reverted_back ? 'Mark Not Reverted' : 'Mark Reverted')}
                                </Button>
                            )}
                        </div>
                    </form>
                </div>
            </div>
        </Modal >
    )
}
